// src/components/admin/AdminSettings.tsx - VERSIÓN FINAL COMPLETA
import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './AdminSettings.css';
import apiElCristo from '../../service/api_elcristo';

interface ModalConfig {
  enabled: boolean;
  logo: string;
  title: string;
  message: string;
  showDate: boolean;
  showSignature?: boolean;
  signatureText?: string | string[];
  date: string;
  startDate: string;
  endDate: string;
  showOnLoad: boolean;
  documentPath?: string;
}

interface ModalHistoryItem {
  id: string;
  title: string;
  message: string;
  date: string;
  startDate: string;
  endDate: string;
  createdAt: string;
  createdBy: string;
}

const AdminSettings: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'current' | 'history'>('current');
  const [showPersistenceInfo, setShowPersistenceInfo] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false); // ← NUEVO

  const [backups, setBackups] = useState<any[]>([]);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [modalHistory, setModalHistory] = useState<ModalHistoryItem[]>([]);

  const [modalConfig, setModalConfig] = useState<ModalConfig>({
    enabled: false,
    logo: '/assets/kidsa/assets/img/logo/logoheader.png',
    title: 'Comunicado Importante',
    message: '<p>Contenido del comunicado...</p>',
    showDate: true,
    date: new Date().toISOString().split('T')[0],
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    showOnLoad: true
  });

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link'],
      ['clean']
    ]
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline',
    'list', 'bullet', 'align', 'link'
  ];

  useEffect(() => {
    loadSettings();
    loadBackups();
  }, []);

  const normalizeSignatureText = (text?: string | string[]): string => {
    if (!text) return '';
    if (Array.isArray(text)) return text.join('\n');
    return text;
  };

  const loadSettings = async () => {
    try {
      setLoading(true);
      const content = await apiElCristo.getSiteContent();
      
      if (content.header?.modalConfig) {
        setModalConfig({
          ...content.header.modalConfig,
          showOnLoad: content.header.modalConfig.showOnLoad ?? true,
          signatureText: normalizeSignatureText(content.header.modalConfig.signatureText)
        });
      }
    } catch (err) {
      console.error('Error loading settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadBackups = async () => {
    try {
      setLoadingBackups(true);
      const result = await apiElCristo.listBackups();
      setBackups(result.backups || []);
    } catch (err) {
      console.error('Error loading backups:', err);
    } finally {
      setLoadingBackups(false);
    }
  };

  const handleSaveModal = async () => {
    try {
      setSaving(true);
      setError('');
      
      const currentContent = await apiElCristo.getSiteContent();
      
      await apiElCristo.updateSiteContent({
        header: {
          ...currentContent.header,
          modalConfig: modalConfig
        }
      });
      
      const newHistoryItem: ModalHistoryItem = {
        id: Date.now().toString(),
        title: modalConfig.title,
        message: modalConfig.message,
        date: modalConfig.date,
        startDate: modalConfig.startDate,
        endDate: modalConfig.endDate,
        createdAt: new Date().toISOString(),
        createdBy: JSON.parse(localStorage.getItem('elcristo_user') || '{}').name || 'Usuario'
      };
      setModalHistory([newHistoryItem, ...modalHistory]);
      
      setSuccess('✅ Configuración guardada exitosamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreDefault = async () => {
    try {
      setSaving(true);
      setError('');
      
      await apiElCristo.restoreDefaultContent();
      
      setSuccess('✅ Contenido restaurado');
      setShowRestoreModal(false);
      
      await loadSettings();
      await loadBackups();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Error al restaurar');
    } finally {
      setSaving(false);
    }
  };

  const loadModalFromHistory = (item: ModalHistoryItem) => {
    setModalConfig({
      ...modalConfig,
      title: item.title,
      message: item.message,
      date: item.date,
      startDate: item.startDate,
      endDate: item.endDate
    });
    setActiveTab('current');
    setSuccess('✅ Modal cargada. Recuerda guardar.');
    setTimeout(() => setSuccess(''), 3000);
  };

  // ← NUEVA FUNCIÓN: Upload de logo
  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      setError('Solo se permiten archivos PNG, JPG o SVG');
      setTimeout(() => setError(''), 3000);
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setError('El archivo debe ser menor a 2MB');
      setTimeout(() => setError(''), 3000);
      return;
    }

    try {
      setUploadingLogo(true);
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'logos');
      
      const response = await fetch('https://backend.anubisai.net/api/elcristo/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('elcristo_token')}`
        },
        body: formData
      });

      if (!response.ok) throw new Error('Error al subir archivo');
      
      const data = await response.json();
      
      setModalConfig({
        ...modalConfig,
        logo: data.url || data.path
      });
      
      setSuccess('✅ Logo subido exitosamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Error al subir logo');
      setTimeout(() => setError(''), 3000);
    } finally {
      setUploadingLogo(false);
    }
  };

  if (loading) {
    return (
      <div className="elcristo-admin-content">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="elcristo-admin-content">
        <div className="elcristo-content-header">
          <div>
            <h1><i className="fas fa-cog"></i> Configuración del Sistema</h1>
            <p>Gestiona la modal de avisos y restaura contenido</p>
          </div>
        </div>

        {success && (
          <div className="alert alert-success">
            <i className="fas fa-check-circle"></i>
            {success}
          </div>
        )}

        {error && (
          <div className="alert alert-error">
            <i className="fas fa-exclamation-circle"></i>
            {error}
          </div>
        )}

        {/* Rollback */}
        <div className="card">
          <div className="card-header">
            <h3><i className="fas fa-undo"></i> Punto de Restauración</h3>
          </div>
          <div className="card-body">
            <p className="hint">Restaura el contenido a valores por defecto (se creará backup).</p>

            <div className="btn-group">
              <button onClick={() => setShowRestoreModal(true)} className="btn btn-danger">
                <i className="fas fa-exclamation-triangle"></i>
                Restaurar Default
              </button>

              <button onClick={loadBackups} className="btn btn-secondary" disabled={loadingBackups}>
                {loadingBackups ? <><div className="spinner-mini"></div> Cargando...</> : <><i className="fas fa-sync"></i> Recargar</>}
              </button>

              <span className="text-muted">
                <i className="fas fa-database"></i> {backups.length} backup(s)
              </span>
            </div>

            {backups.length > 0 && (
              <div className="backup-list">
                {backups.slice(0, 3).map((backup, index) => (
                  <div key={index} className="backup-item">
                    <span><i className="fas fa-file-archive"></i> {backup.filename}</span>
                    <small>{new Date(backup.created_at).toLocaleString('es-CL')}</small>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="card">
          <div className="tabs">
            <button
              onClick={() => setActiveTab('current')}
              className={`tab ${activeTab === 'current' ? 'active' : ''}`}
            >
              <i className="fas fa-bullhorn"></i> Modal Actual
            </button>

            <button
              onClick={() => setActiveTab('history')}
              className={`tab ${activeTab === 'history' ? 'active' : ''}`}
            >
              <i className="fas fa-history"></i> Historial 
              {modalHistory.length > 0 && <span className="badge">{modalHistory.length}</span>}
            </button>
          </div>

          <div className="card-body">
            {activeTab === 'current' && (
              <>
                <div className="form-section">
                  <h3><i className="fas fa-bullhorn"></i> Modal de Avisos</h3>
                  <p className="hint">Configura la modal de la página principal.</p>

                  <label className="switch-inline">
                    <input
                      type="checkbox"
                      checked={modalConfig.enabled}
                      onChange={(e) => setModalConfig({ ...modalConfig, enabled: e.target.checked })}
                    />
                    <span className="slider"></span>
                    <span className="label">{modalConfig.enabled ? 'Modal Habilitada' : 'Modal Deshabilitada'}</span>
                  </label>

                  {modalConfig.enabled && (
                    <>
                      <div className="form-row">
                        {/* ← LOGO CON UPLOAD */}
                        <div className="form-group">
                          <label><i className="fas fa-image"></i> Logo</label>
                          
                          {modalConfig.logo && (
                            <div className="logo-preview">
                              <img src={modalConfig.logo} alt="Logo preview" />
                            </div>
                          )}
                          
                          <div className="logo-upload-group">
                            <input
                              type="text"
                              value={modalConfig.logo}
                              onChange={(e) => setModalConfig({ ...modalConfig, logo: e.target.value })}
                              placeholder="/assets/kidsa/assets/img/logo/logoheader.png"
                              className="compact"
                            />
                            
                            <div className="upload-btn-wrapper">
                              <button 
                                type="button" 
                                className="btn btn-upload"
                                disabled={uploadingLogo}
                                onClick={() => document.getElementById('logo-upload-input')?.click()}
                              >
                                {uploadingLogo ? (
                                  <><div className="spinner-mini"></div> Subiendo...</>
                                ) : (
                                  <><i className="fas fa-upload"></i> Subir</>
                                )}
                              </button>
                              <input
                                id="logo-upload-input"
                                type="file"
                                accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                                onChange={handleLogoUpload}
                                style={{ display: 'none' }}
                              />
                            </div>
                          </div>
                          
                          <small className="hint">
                            <i className="fas fa-info-circle"></i> Formatos: PNG, JPG, SVG (máx 2MB)
                          </small>
                        </div>

                        <div className="form-group flex-2">
                          <label><i className="fas fa-heading"></i> Título</label>
                          <input
                            type="text"
                            value={modalConfig.title}
                            onChange={(e) => setModalConfig({ ...modalConfig, title: e.target.value })}
                            placeholder="Comunicado Importante"
                            className="compact"
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label><i className="fas fa-align-left"></i> Mensaje</label>
                        <div className="quill-wrapper">
                          <ReactQuill
                            theme="snow"
                            value={modalConfig.message}
                            onChange={(content) => setModalConfig({ ...modalConfig, message: content })}
                            modules={quillModules}
                            formats={quillFormats}
                            placeholder="Escribe el contenido del comunicado..."
                          />
                        </div>
                        <small className="hint">Use la barra de herramientas para dar formato</small>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label><i className="far fa-calendar-plus"></i> Fecha Inicio</label>
                          <input
                            type="date"
                            value={modalConfig.startDate}
                            onChange={(e) => setModalConfig({ ...modalConfig, startDate: e.target.value })}
                            className="compact"
                          />
                        </div>

                        <div className="form-group">
                          <label><i className="far fa-calendar-minus"></i> Fecha Término</label>
                          <input
                            type="date"
                            value={modalConfig.endDate}
                            onChange={(e) => setModalConfig({ ...modalConfig, endDate: e.target.value })}
                            className="compact"
                          />
                        </div>
                      </div>

                      <div className="persistence-control">
                        <div className="persistence-header">
                          <label><i className="fas fa-sync-alt"></i> Modo de Persistencia</label>
                          <button
                            type="button"
                            className="btn-info-mini"
                            onClick={() => setShowPersistenceInfo(!showPersistenceInfo)}
                          >
                            <i className="fas fa-info-circle"></i>
                          </button>
                        </div>

                        <label className="switch-inline">
                          <input
                            type="checkbox"
                            checked={!modalConfig.showOnLoad}
                            onChange={(e) => setModalConfig({ ...modalConfig, showOnLoad: !e.target.checked })}
                          />
                          <span className="slider pro"></span>
                          <span className="label">
                            {modalConfig.showOnLoad ? 
                              <><i className="fas fa-redo"></i> Cada visita</> : 
                              <><i className="fas fa-check-circle"></i> Una vez por sesión</>
                            }
                          </span>
                        </label>

                        {showPersistenceInfo && (
                          <div className="info-panel">
                            <strong><i className="fas fa-lightbulb"></i> ¿Qué es esto?</strong>
                            <ul>
                              <li><strong>Cada visita:</strong> Modal aparece siempre al recargar (útil en desarrollo)</li>
                              <li><strong>Una vez por sesión:</strong> Modal aparece solo la primera vez (recomendado producción)</li>
                            </ul>
                            <small><i className="fas fa-info-circle"></i> El globo flotante siempre aparece si la modal está activa</small>
                          </div>
                        )}
                      </div>

                      <div className="checkbox-group">
                        <label className="checkbox-inline">
                          <input
                            type="checkbox"
                            checked={modalConfig.showDate}
                            onChange={(e) => setModalConfig({ ...modalConfig, showDate: e.target.checked })}
                          />
                          <span></span>
                          <i className="far fa-calendar-alt"></i> Mostrar fecha
                        </label>

                        <label className="checkbox-inline">
                          <input
                            type="checkbox"
                            checked={modalConfig.showSignature || false}
                            onChange={(e) => setModalConfig({ ...modalConfig, showSignature: e.target.checked })}
                          />
                          <span></span>
                          <i className="fas fa-signature"></i> Mostrar firma
                        </label>
                      </div>

                      {modalConfig.showDate && (
                        <div className="form-group" style={{ maxWidth: '220px' }}>
                          <label><i className="far fa-calendar-check"></i> Fecha a mostrar</label>
                          <input
                            type="date"
                            value={modalConfig.date}
                            onChange={(e) => setModalConfig({ ...modalConfig, date: e.target.value })}
                            className="compact"
                          />
                        </div>
                      )}

                      {modalConfig.showSignature && (
                        <div className="form-group">
                          <label><i className="fas fa-signature"></i> Texto de Firma</label>
                          <textarea
                            value={typeof modalConfig.signatureText === 'string' ? modalConfig.signatureText : ''}
                            onChange={(e) => setModalConfig({ ...modalConfig, signatureText: e.target.value })}
                            rows={2}
                            placeholder="Atentamente,&#10;Director del Colegio"
                            className="compact"
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="form-actions">
                  <button onClick={handleSaveModal} className="btn btn-primary" disabled={saving}>
                    {saving ? <><div className="spinner-mini"></div> Guardando...</> : <><i className="fas fa-save"></i> Guardar</>}
                  </button>

                  <button onClick={loadSettings} className="btn btn-secondary" disabled={saving}>
                    <i className="fas fa-redo"></i> Recargar
                  </button>
                </div>

                {modalConfig.enabled && (
                  <div className="preview-mini">
                    <h4><i className="fas fa-eye"></i> Vista Previa</h4>
                    <div className="preview-card">
                      <div className="preview-header">
                        {modalConfig.logo && <img src={modalConfig.logo} alt="Logo" />}
                        <h5>{modalConfig.title}</h5>
                      </div>
                      <div className="preview-body" dangerouslySetInnerHTML={{ __html: modalConfig.message }} />
                      {modalConfig.showDate && (
                        <div className="preview-date">
                          <i className="fas fa-calendar-alt"></i> {new Date(modalConfig.date).toLocaleDateString('es-CL')}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            {activeTab === 'history' && (
              <div className="form-section">
                <h3><i className="fas fa-history"></i> Historial de Modales</h3>
                <p className="hint">Click para reutilizar una configuración anterior.</p>

                {modalHistory.length === 0 ? (
                  <div className="empty-state">
                    <i className="fas fa-inbox"></i>
                    <p>No hay modales en el historial</p>
                    <small>Las modales guardadas aparecerán aquí</small>
                  </div>
                ) : (
                  <div className="history-list">
                    {modalHistory.map((item) => (
                      <div key={item.id} className="history-item" onClick={() => loadModalFromHistory(item)}>
                        <div className="history-header">
                          <h4><i className="fas fa-bullhorn"></i> {item.title}</h4>
                          <span className="date">{new Date(item.createdAt).toLocaleDateString('es-CL')}</span>
                        </div>
                        <div className="history-preview" dangerouslySetInnerHTML={{ __html: item.message }} />
                        <div className="history-footer">
                          <i className="far fa-calendar"></i> {new Date(item.startDate).toLocaleDateString('es-CL')} - {new Date(item.endDate).toLocaleDateString('es-CL')}
                          <span>•</span>
                          <i className="fas fa-user"></i> {item.createdBy}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {showRestoreModal && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowRestoreModal(false)}>
          <div className="modal-dialog compact">
            <div className="modal-content">
              <div className="modal-header danger">
                <h3><i className="fas fa-exclamation-triangle"></i> Confirmar Restauración</h3>
                <button onClick={() => setShowRestoreModal(false)} className="close-btn">×</button>
              </div>

              <div className="modal-body">
                <div className="warning-box">
                  <i className="fas fa-exclamation-triangle"></i>
                  <div>
                    <strong>Esta acción no se puede deshacer</strong>
                    <p>Se creará un backup automático antes de restaurar</p>
                  </div>
                </div>

                <p>Se restaurará <strong>todo el contenido del sitio</strong> a valores por defecto:</p>
                <ul className="compact-list">
                  <li>Hero section</li>
                  <li>Sección Nosotros</li>
                  <li>Programas educativos</li>
                  <li>Equipo docente</li>
                  <li>Noticias</li>
                  <li>Header y menú</li>
                  <li>Configuración de modal</li>
                </ul>
              </div>

              <div className="modal-footer">
                <button onClick={() => setShowRestoreModal(false)} className="btn btn-secondary" disabled={saving}>
                  Cancelar
                </button>
                <button onClick={handleRestoreDefault} className="btn btn-danger" disabled={saving}>
                  {saving ? <><div className="spinner-mini"></div> Restaurando...</> : <><i className="fas fa-undo"></i> Sí, Restaurar</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminSettings;